import Button, { ActionButton } from '@bugbug/core/components/Button';
import React, { useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Header, Footer, ErrorInfo, DeleteIcon } from '~/components/modals/Modal';
import useModal from '~/hooks/useModal';
import { StepsActions } from '~/modules/steps/steps.redux';
import { useDeleteCurrentTestPartials } from '~/modules/tests/hooks/useDeleteCurrentTestPartials/useDeleteCurrentTestPartials';
import { isInternalServerError } from '~/services/api/utils';
import { getSelectedPartialsLabel } from '~/utils/misc';

import { Container, Content } from './DeleteTestPartialsModal.styled';

interface DeleteTestPartialsModalProps {
  className?: string;
  onSuccess?: () => void;
  testId: string;
  steps?: string[];
  groups?: string[];
}

const DeleteTestPartialsModal = ({
  testId,
  steps = [],
  groups = [],
  onSuccess,
}: DeleteTestPartialsModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modal = useModal();
  const [deletePartialsFromTest, deletePartialsFromTestRequest] = useDeleteCurrentTestPartials();

  const handleDeleteConfirm = useCallback(async () => {
    await deletePartialsFromTest({
      testId,
      stepsIds: steps,
      groupsIds: groups,
    });

    modal.hide();
    dispatch(StepsActions.resetCurrentStep());
    onSuccess?.();
  }, [deletePartialsFromTest, testId, steps, groups, modal, onSuccess, dispatch]);

  const partialsLabel = getSelectedPartialsLabel(steps.length, groups.length);

  return (
    <Container>
      <Content>
        <DeleteIcon />
        <div>
          <Header>
            {t('deleteTestPartialsModal.title', 'Delete {{partialsLabel}}?', {
              partialsLabel,
            })}
          </Header>
          <p>
            <Trans
              i18nKey="deleteTestPartialsModal.description"
              defaults="Are you sure you want to delete {{ partialsLabel }}? You <strong>can't undo this.</strong>"
              values={{ partialsLabel }}
            />
          </p>
        </div>
      </Content>
      <Footer>
        <ErrorInfo isVisible={isInternalServerError(deletePartialsFromTestRequest.error)} />
        <Button onClick={modal.hide}>{t('deleteTestPartialsModal.cancel', 'Cancel')}</Button>
        <ActionButton
          onClick={handleDeleteConfirm}
          variant="primary"
          pending={deletePartialsFromTestRequest.isLoading}
        >
          {t('deleteTestPartialsModal.button', 'Delete {{partialsLabel}}', {
            partialsLabel,
          })}
        </ActionButton>
      </Footer>
    </Container>
  );
};

export default DeleteTestPartialsModal;
